import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { isAfter } from 'date-fns';
import { isFunction } from 'lodash';
import {
  AnnotationIcon,
  DeleteIcon,
  ModelIcon,
  AnnotationEmptyIcon,
} from 'icons';
import { useContextManager } from 'apps/context/hooks';
import { CADPanelWidth } from 'styles/constants';
import {
  EmptyText,
  Icon,
  Paragraph,
} from 'components';
import { useCADManager } from 'hooks';
import { subscriptionStatusObservable } from 'core/client';
import { frameStore } from 'apps/components/Frame/appManager';
import RelativeTime from 'components/RelativeTime';
import { SpinnerStyled } from 'components/Button/Button.styled';
import { AnnotationWays } from '../toolBar';
import { VoidFn } from 'types';

type Annotation = {
  createdBy: string;
  body: string;
  id: string;
  timestamp: string;
  kind: string;
};

enum AnnotationEnum {
  AnnotatePart = 'AnnotatePart',
  AnnotateGeometry = 'AnnotateGeometry',
}

export const AnnotationsPanel = observer(() => {
  const {
    closePanel,
    isActivePart,
    viewerControl,
    isActiveGeometry,
    showAnnotateGeometry,
    showAnnotatePart,
    showAnnotationsEyes,
    shouldShowAnnotationPin,
    handleClickAnnotationEye,
    handleClickAnnotationPart,
    handleClickAnnotationGeometry,
  } = useCADManager();

  const startAnnotationDirectly =
    [showAnnotateGeometry, showAnnotatePart].filter(Boolean).length === 1;

  const { AnnotatePart, AnnotateGeometry } = AnnotationEnum;

  const contextManager: Record<string, any> = useContextManager();
  const { annotations = [], loading } = contextManager;
  const [operateId, setOperateId] = useState(null);
  const [selectGeometryOrPart, setSelectGeometryOrPart] = useState(false);

  const hasAnnotationWays =
    contextManager.annotationsAvailable &&
    subscriptionStatusObservable.status === 'connected';

  const deleteAnnotation = (id: string) => {
    if (viewerControl.isPinExpanded(id)) {
      viewerControl?.TogglePinExpand(id);
    }
    contextManager.DeleteAnnotation(id);
  };

  const renderAnnotations: Annotation[] = annotations?.filter((annotation: Annotation) => annotation.kind === 'NOTE').sort(
    (pre: Annotation, curr: Annotation) =>
      isAfter(new Date(pre.timestamp), new Date(curr.timestamp)),
  );

  const hasAnnotations = !!renderAnnotations?.length;

  const getActiveMethod = () => {
    if (isActivePart) return 'part';

    if (isActiveGeometry) return 'geometry';

    return null;
  };

  const getSingleAnnotationKey = () => {
    if (!startAnnotationDirectly) return null;

    return showAnnotateGeometry ? AnnotateGeometry : AnnotatePart;
  };

  const singleAnnotationMap: Record<AnnotationEnum, VoidFn> = {
    [AnnotatePart]: handleClickAnnotationPart,
    [AnnotateGeometry]: handleClickAnnotationGeometry,
  };

  const handleClickNewAnnotation = () => {
    if (startAnnotationDirectly) {
      const clickFn = singleAnnotationMap[getSingleAnnotationKey()];
      if (isFunction(clickFn)) {
        clickFn();
        return;
      }
    }

    setSelectGeometryOrPart(true);
  };

  const activeType = getActiveMethod();

  return (
    <div
      className='h-full'
      style={{
        width: CADPanelWidth,
      }}
    >
      <div className='flex px-4 my-2 w-full justify-between items-center'>
        <h2 className='text-sm text-wfBlack'>Annotations</h2>
        <div className='flex items-center cursor-pointer'>
          {showAnnotationsEyes && (
            <div
              onClick={handleClickAnnotationEye}
              className='border flex-center mr-2 border-wfGrey100 w-6 h-6 rounded-full'
            >
              <Icon
                size='default'
                name={
                  viewerControl.viewer.notePad.visible ? 'view_off' : 'view'
                }
              />
            </div>
          )}
          <Icon
            name='close'
            size='small'
            onClick={closePanel}
            style={{ width: 12, height: 12 }}
          />
        </div>
      </div>
      {hasAnnotationWays && (
        <>
          {selectGeometryOrPart || shouldShowAnnotationPin ? (
            <div className='py-2 px-4 border-t border-wfGrey100'>
              <AnnotationWays />
            </div>
          ) : (
            <p
              onClick={handleClickNewAnnotation}
              className='py-2 px-4 text-sm text-wfBlue cursor-pointer border-t border-wfGrey100'
            >
              New Annotation
            </p>
          )}

          {activeType && (
            <div className='flex w-full text-sm py-2 px-4 bg-wfGrey100 text-wfBlack items-center'>
              <ModelIcon className='mr-1' />
              {`Select  ${activeType}  from model...`}
            </div>
          )}
        </>
      )}

      {hasAnnotations ? (
        <div className='flex flex-col h-full overflow-y-scroll'>
          {renderAnnotations.map((item, i) => {
            const { createdBy: userId, body, id, timestamp } = item;
            const isFirstAnnotation = i === 0;

            const note = viewerControl.viewer.notePad.GetNote(id);
            const forCadItem = viewerControl.findCadItemForNote(note);
            let bodyStyle = {};
            if (!forCadItem) {
              bodyStyle = {
                textDecoration: 'line-through',
              };
            }

            const currentUser =
              contextManager?.members[userId] ||
              frameStore.getUserInfoById(userId);

            const createdBy = typeof userId === 'string' ? currentUser : userId;
            const creatorName =
              createdBy?.fullName || createdBy?.username?.replace('@', '');

            const canDelete = contextManager?.context?.canDeleteAnnotation(
              item,
              userId,
            );

            return (
              <div
                key={id}
                className={`${
                  isFirstAnnotation ? 'border-y' : 'border-b'
                } border-wfGrey100 py-2 px-4 flex  cursor-pointer overscroll-contain`}
                onClick={() => {
                  viewerControl?.TogglePinExpand(id);
                }}
              >
                <div className='border border-wfGrey100 flex-center p-2 rounded w-8 h-8 mr-2'>
                  <AnnotationIcon width={24} height={24} />
                </div>
                <div className='text-wfBlack w-full'>
                  <div className='flex gap-1 items-center'>
                    {creatorName && (
                      <>
                        <span className=' text-sm'>{creatorName}</span>
                        <span className=' text-wfGrey700'>—</span>
                      </>
                    )}
                    <RelativeTime date={timestamp} />
                  </div>
                  <div>
                    <p className=' text-sm' style={bodyStyle}>{body}</p>
                    {!forCadItem && <Paragraph size='xsmall' variant='light'>(outdated)</Paragraph>}
                  </div>

                  {canDelete && (
                    <div className='w-full flex justify-end'>
                      {loading && operateId === id ? (
                        <SpinnerStyled size='default' autoColor />
                      ) : (
                        <div
                          className=' border cursor-pointer border-wfGrey100 w-8 h-8 p-2 flex-center rounded-full hover:bg-wfRed'
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOperateId(id);
                            deleteAnnotation(id);
                          }}
                        >
                          <DeleteIcon width={18} height={18} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className='flex-y-center w-full border-y border-wfGrey100  m-0 py-8 h-auto'>
          <AnnotationEmptyIcon />
          <EmptyText text='No annotations yet' />
        </div>
      )}
    </div>
  );
});
